"use client";

import { useEffect, useMemo, useState } from "react";
import { useFetcher } from "react-router";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { Button } from "~/components/ui/button";
import { saveAnswers } from "~/hec/telehealth/answer-store.client";
import { TelehealthGetStartedButton } from "~/hec/telehealth/components/get-started-button";
import { QuestionAtom } from "~/hec/telehealth/components/question-atom";
import {
  isAnswerable,
  isAnswered,
  isPageValid,
  visibleQuestions,
} from "~/hec/telehealth/intake-flow";
import { type FirstQuestionHandoff } from "~/hec/telehealth/intake-routing";
import { useOptionalTelehealthPdpPrice } from "~/hec/telehealth/pdp-price-context";
import { evaluatePredicate } from "~/hec/telehealth/predicate";
import {
  type AnswerMap,
  type AnswerValue,
  type Page,
  type Question,
  type Questionnaire,
  pageById,
} from "~/hec/telehealth/schema";
import {
  isTreatmentAvailable,
  treatmentBySlug,
} from "~/hec/telehealth/treatments";
import { useStartIntake } from "~/hec/telehealth/use-start-intake";
import { cn } from "~/lib/ui";

interface FirstQuestionProps {
  /** Builder injects this on `noWrap` components — must spread on the root. */
  attributes?: Record<string, unknown>;
  treatment?: string;
  /** A whole questionnaire module/page to render as a group. Wins over `questionId`. */
  pageId?: string;
  questionId?: string;
  heading?: string;
  subheading?: string;
  ctaLabel?: string;
  genericMode?: boolean;
}

interface QuestionnaireResponse {
  questionnaire: Questionnaire | null;
}

/**
 * First-Question Builder block (Chunk 2 / ET-2). A business user drops it on any
 * page and picks a treatment plus a first-question **module** to present — either
 * a single `questionId` or a whole `pageId` group (e.g. `p1_height_weight`, which
 * renders every input on that page). It renders the module with a Get Started
 * CTA; answering routes to `/intake/[treatment]` with the answers carried over
 * (and persisted to localStorage for return visits — ET-51). `pageId` wins
 * over `questionId`.
 */
export function FirstQuestion({
  attributes,
  treatment = "weight-loss",
  pageId,
  questionId,
  heading = "Let's see if treatment is right for you",
  subheading,
  ctaLabel = "Get Started",
  genericMode = false,
}: FirstQuestionProps) {
  const startIntake = useStartIntake();
  // Present only when this block renders inside a treatment PDP's buy column.
  const pdpCtaContext = useOptionalTelehealthPdpPrice()?.ctaContext;
  const fetcher = useFetcher<QuestionnaireResponse>();
  const [answers, setAnswers] = useState<AnswerMap>({});

  const treatmentDef = treatmentBySlug(treatment);
  const conditionCode = treatmentDef?.conditionCode ?? "";
  // Fetch the questionnaire when a module is configured (a page group or a
  // single question). `pageId` takes precedence over `questionId`.
  const showModule =
    !genericMode && !!conditionCode && (!!pageId || !!questionId);

  useEffect(() => {
    if (!showModule) return;
    // Known limitation: the `fetcher.data === undefined` gate loads once and
    // never refetches. If `treatment` (→ `conditionCode`) changes after the
    // first load — e.g. live-switching treatment in the Builder editor — the
    // block keeps rendering the previous condition's module. Harmless while the
    // Builder enum only offers weight-loss; when a second condition exists,
    // re-key the fetcher on `conditionCode` so it reloads on change.
    if (fetcher.state === "idle" && fetcher.data === undefined) {
      fetcher.load(
        `/resources/telehealth-questionnaire?condition=${encodeURIComponent(conditionCode)}`,
      );
    }
  }, [showModule, conditionCode, fetcher]);

  const questionnaire = fetcher.data?.questionnaire ?? null;

  // Group mode: a whole page module resolved by `pageId`.
  const page = useMemo<Page | null>(() => {
    if (genericMode || !pageId || !questionnaire) return null;
    return pageById(questionnaire, pageId);
  }, [genericMode, pageId, questionnaire]);

  // Single mode: one question by `questionId` (only when no `pageId`).
  const singleQuestion = useMemo<Question | null>(() => {
    if (genericMode || pageId || !questionId || !questionnaire) return null;
    for (const p of questionnaire.questionnaire.pages) {
      const match = p.questions.find(q => q.questionId === questionId);
      if (!match) continue;
      // AC-6: a conditional question can't be the first module unless its
      // prerequisite context is already satisfied. A single first question has
      // no prior context, so one gated by `visibleWhen` on another answer is
      // invalid config → null (safe fallback, AC-7), never silently shown.
      return evaluatePredicate(match.visibleWhen, {}) ? match : null;
    }
    return null;
  }, [genericMode, pageId, questionId, questionnaire]);

  // The answerable inputs of the group, honoring intra-group `visibleWhen`
  // branching. COMPUTED (bmi) / IMAGE atoms aren't inputs, so they're excluded.
  const groupQuestions = useMemo<Question[]>(
    () => (page ? visibleQuestions(page, answers).filter(isAnswerable) : []),
    [page, answers],
  );

  const loadingModule = showModule && fetcher.data === undefined;
  // A page with no answerable inputs at all (e.g. only COMPUTED/IMAGE) is not a
  // usable module — treat it like missing config (fallback), not an empty group.
  const groupIsRenderable = page ? page.questions.some(isAnswerable) : false;
  const hasModule = groupIsRenderable || Boolean(singleQuestion);
  // Gate the CTA on the module being fully answered (required + format for the
  // group; a non-empty value for a single question). If nothing resolved
  // (missing/invalid config), the CTA still routes onward — safe fallback. While
  // the module is still loading, hold the CTA so a fast click can't skip it.
  const moduleAnswered =
    page && groupIsRenderable
      ? isPageValid(page, answers)
      : singleQuestion
        ? isAnswered(answers[singleQuestion.questionId])
        : true;
  const canContinue =
    !loadingModule && (genericMode || !hasModule || moduleAnswered);

  const treatmentSlug = treatmentDef?.slug ?? treatment;

  const setAnswer = (id: string, value: AnswerValue) =>
    setAnswers(prev => ({ ...prev, [id]: value }));

  const handleStart = () => {
    // ET-12 backstop: a surface is expected to only render an active Start CTA
    // for an available treatment (surface-specific CTA placement is out of
    // ET-12 scope), and `startIntake` gates availability again internally. Guard
    // here too so a misconfigured module short-circuits BEFORE the localStorage
    // persist / analytics fire — an unavailable/unknown treatment does no work
    // and starts no intake. Direct navigation to `/intake/[treatment]` still
    // shows the route's visible unavailable/not-found state.
    if (!isTreatmentAvailable(treatmentSlug)) return;

    // ET-3/ET-14: discovery-to-intake handoff context — which module (this one)
    // and which CTA sent the shopper here. The shared ET-12 `startIntake`
    // contract fires `treatment_cta_clicked` and routes to `/intake/[treatment]`
    // carrying these non-PHI params.
    const start = {
      treatment: treatmentSlug,
      ...FIRST_QUESTION_SOURCE,
      // ET-216: a PDP buy column publishes its own attribution, so a CTA
      // authored there reports `src=pdp` like the coded one it replaces
      // instead of reattributing that traffic to the block.
      ...(pdpCtaContext ?? {}),
    };

    // Collect only the configured module's answered fields.
    const moduleAnswers: AnswerMap = {};
    const moduleQuestions = page
      ? groupQuestions
      : singleQuestion
        ? [singleQuestion]
        : [];
    for (const q of moduleQuestions) {
      const v = answers[q.questionId];
      if (isAnswered(v)) moduleAnswers[q.questionId] = v;
    }

    if (Object.keys(moduleAnswers).length > 0) {
      // Persist the module's answers to localStorage (ET-51, keyed by
      // conditionCode); the intake form preselects/restores them on arrival.
      // The module's own analytics events are gone — they weren't part of the
      // MarTech ingest spec.
      saveAnswers(conditionCode, {
        conditionCode,
        questionnaireId: questionnaire?.questionnaireId,
        templateVersion: questionnaire?.templateVersion ?? "0",
        answers: moduleAnswers,
      });
      // Typed against the shared handoff contract so producer + consumer
      // (intake route) can't drift; carried as RR navigation state.
      const firstQuestion: FirstQuestionHandoff = { answers: moduleAnswers };
      startIntake(start, { state: { firstQuestion } });
      return;
    }
    startIntake(start);
  };

  // ET-216: generic mode with the heading AND subheading cleared is an author
  // asking for the bare CTA — the shape the treatment PDP's pricing section
  // needs, where the card chrome below would box a lone button inside the buy
  // column. Uses the same button the coded PDP column renders so the two can't
  // drift on styling. `heading` ships with a default, so an empty one is a
  // deliberate choice, not an unconfigured entry.
  if (genericMode && !heading && !subheading) {
    const { source, ctaLocation } = pdpCtaContext ?? FIRST_QUESTION_SOURCE;
    return (
      <TelehealthGetStartedButton
        treatment={treatmentSlug}
        source={source}
        ctaLocation={ctaLocation}
        label={ctaLabel}
        attributes={attributes}
        data-testid="first-question-get-started"
      />
    );
  }

  return (
    <section
      {...attributes}
      // Builder's injected `className` is merged, not clobbered — a later
      // `className` prop wins outright in JSX, and the editor's
      // responsive-visibility classes are functional (`display: none`).
      className={cn(
        "mx-auto flex w-full max-w-xl flex-col gap-6 rounded-2xl border border-blue-03 bg-white p-6 shadow-sm sm:p-8",
        (attributes as { className?: string } | undefined)?.className,
      )}
      data-testid="telehealth-first-question"
    >
      {heading ? (
        <div className="flex flex-col gap-1">
          <h2 className="font-grotesque text-2xl font-bold text-deep-blue">
            {heading}
          </h2>
          {subheading ? (
            <p className="text-base text-neutral-10">{subheading}</p>
          ) : null}
        </div>
      ) : null}

      {loadingModule ? (
        <div
          className="h-32 w-full animate-pulse rounded-md bg-deep-blue/5"
          data-testid="first-question-loading"
        />
      ) : null}

      {groupIsRenderable ? (
        <div className="flex flex-col gap-6" data-testid="first-question-group">
          {groupQuestions.map(q => (
            <QuestionAtom
              key={q.questionId}
              question={q}
              value={answers[q.questionId] ?? null}
              onChange={value => setAnswer(q.questionId, value)}
            />
          ))}
        </div>
      ) : null}

      {singleQuestion ? (
        <QuestionAtom
          question={singleQuestion}
          value={answers[singleQuestion.questionId] ?? null}
          onChange={value => setAnswer(singleQuestion.questionId, value)}
        />
      ) : null}

      {showModule && !loadingModule && !hasModule ? (
        <p className="text-sm text-neutral-9">
          We couldn&apos;t load this question right now.
        </p>
      ) : null}

      <Button
        variant="cta"
        size="5"
        disabled={!canContinue}
        onClick={handleStart}
        data-testid="first-question-get-started"
      >
        {ctaLabel}
      </Button>
    </section>
  );
}

/** This block's own attribution, used outside a PDP buy column. */
const FIRST_QUESTION_SOURCE = {
  source: "first-question",
  ctaLocation: "get-started",
} as const;

export const FirstQuestionDefinition = {
  component: FirstQuestion,
  name: "TelehealthFirstQuestion",
  noWrap: true,
  inputs: [
    {
      name: "treatment",
      type: "string",
      enum: ["weight-loss"],
      defaultValue: "weight-loss",
      helperText: "Treatment whose questionnaire to read.",
    },
    {
      name: "genericMode",
      type: "boolean",
      defaultValue: false,
      helperText:
        "Show only a Get Started button (no question) that links straight to intake. Hides the Page Id / Question Id fields below.",
    },
    {
      name: "pageId",
      type: "string",
      // Hidden in Generic Mode — a module can't be configured there, so
      // surfacing these fields only invites the contradictory combo.
      showIf: `options.get('genericMode') !== true`,
      helperText:
        "pageId of a questionnaire module/page to render as a group (e.g. p1_height_weight renders all height & weight inputs). Takes precedence over Question Id.",
    },
    {
      name: "questionId",
      type: "string",
      showIf: `options.get('genericMode') !== true`,
      helperText:
        "Single questionId from the THS questionnaire to present first (e.g. an eligibility question). Ignored when Page Id is set.",
    },
    {
      name: "heading",
      type: "string",
      defaultValue: "Let's see if treatment is right for you",
    },
    { name: "subheading", type: "string" },
    { name: "ctaLabel", type: "string", defaultValue: "Get Started" },
  ],
} satisfies RegisteredComponent;
